import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/store/authStore'
// Tabla editable personalizada (ya no usamos react-spreadsheet)
import {
  Product,
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  downloadProductTemplate,
  importProductsExcel,
  getImportStatus,
  suggestProductCommercialRole,
  type ProductCreateInput
} from '@/lib/api/products'
import { categoriesApi, Category } from '@/services/categoriesApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import TagsInput from '@/components/ui/tags-input'
import ProductImageUploader from '@/components/catalog/ProductImageUploader'
import ProductWarrantyFieldGroup from '@/components/catalog/ProductWarrantyFieldGroup'
import { cn } from '@/lib/utils'
import {
  Copy,
  ExternalLink,
  Package,
  Download,
  Upload,
  Table2,
  LayoutGrid,
  Plus,
  Loader2,
  RefreshCw,
  Trash2,
  PlusCircle,
  Search,
  Edit2,
  Share2,
  Tag,
  Bot,
  Brain,
  Zap,
  ShoppingCart,
  X,
  RotateCcw,
  Check,
} from 'lucide-react'
import {
  buildCatalogShareMessage,
  buildWhatsAppShareLink,
  formatMoney,
  getPublicCatalogUrl,
} from '@/lib/catalogLinks'
import { buildPromotionCreateUrl } from '@/lib/promotionLinks'
import { getProductPromotionBadge } from '@/lib/promotionBadges'
import { promotionsApi } from '@/services/promotionsApi'
import CatalogNavTabs from '@/components/catalog/CatalogNavTabs'
import {
  useVerticalUI,
  VerticalProductFields,
  VerticalProductBadge,
  VerticalAvailabilityBadge,
  getVerticalCommercialRoles,
  getVerticalRoleLabel,
} from '@/lib/verticalUI'
import {
  listProductRelations,
  upsertProductRelation,
  deleteProductRelation,
  type ProductCatalogRelation,
} from '@/services/catalogIntelligenceApi'
import {
  type SpreadsheetProductRow,
  mapProductToSpreadsheetRow,
  createEmptySpreadsheetRow,
  updateSpreadsheetRowField,
  removeSpreadsheetRow,
  filterSpreadsheetRows,
  getDirtySpreadsheetRows,
  validateSpreadsheetRow,
  DEFAULT_CATEGORY,
} from '@/lib/catalogSpreadsheet'
import { resolveImageUrl } from '@/utils/imageUrl'
import { extractApiErrorMessage } from '@/utils/apiError'

export const COMMERCIAL_ROLE_OPTIONS = [
  { value: '', label: 'General / Estándar', icon: '📦' },
  { value: 'main', label: 'Plato Principal', icon: '🍔' },
  { value: 'drink', label: 'Bebida', icon: '🥤' },
  { value: 'side', label: 'Acompañante / Extra', icon: '🍟' },
  { value: 'dessert', label: 'Postre', icon: '🍰' },
  { value: 'accessory', label: 'Accesorio / Complemento', icon: '🔌' },
  { value: 'service', label: 'Servicio', icon: '🛠️' },
  { value: 'warranty', label: 'Garantía', icon: '🛡️' },
] as const

function getRoleLabel(role?: string | null, slug?: any) {
  return getVerticalRoleLabel(role, slug)
}

const productSchema = z.object({
  sku: z.string().min(1, 'SKU es requerido'),
  name: z.string().min(1, 'Nombre es requerido'),
  description: z.string().optional(),
  price: z.number().min(0, 'El precio debe ser mayor a 0'),
  currency: z.string().length(3, 'La moneda debe tener 3 letras').default('USD'),
  stock: z.number().int().min(0, 'El stock debe ser mayor o igual a 0'),
  imageUrl: z.string().optional().refine(
    (val) => {
      if (!val || val === '') return true
      if (val.startsWith('/storage/') || val.startsWith('/api/storage/') || val.startsWith('/')) return true
      return z.string().url().safeParse(val).success
    },
    {
      message: 'Debe ser una URL válida o ruta de almacenamiento local (/storage/...)',
    }
  ),
  keywords: z.array(z.string()).default([]),
  category: z.string().default(DEFAULT_CATEGORY),
  commercialRole: z.string().optional().nullable(),
  metadata: z.record(z.any()).optional().nullable(),
})

type ProductFormValues = z.infer<typeof productSchema>

type ViewMode = 'cards' | 'spreadsheet'

export default function ProductsPage() {
  const { profile } = useVerticalUI()
  const commercialRoleConfig = useMemo(
    () => getVerticalCommercialRoles(profile.slug),
    [profile.slug]
  )
  const tenant = useAuthStore((s) => s.tenant)
  const navigate = useNavigate()
  const tenantSlug = tenant?.slug || ''
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>('cards')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState<Product | null>(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importJobId, setImportJobId] = useState<string | null>(null)
  const [importProgress, setImportProgress] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [spreadsheetRows, setSpreadsheetRows] = useState<SpreadsheetProductRow[]>([])
  const [savingSpreadsheet, setSavingSpreadsheet] = useState(false)
const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [categories, setCategories] = useState<Category[]>([])
  const [complements, setComplements] = useState<ProductCatalogRelation[]>([])
  const [loadingComplements, setLoadingComplements] = useState(false)
  const [selectedComplementProductId, setSelectedComplementProductId] = useState('')
  const [addingComplement, setAddingComplement] = useState(false)
  const [isSuggestingRole, setIsSuggestingRole] = useState(false)

  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue, setError, watch } = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema as any),
    defaultValues: {
      sku: '',
      name: '',
      description: '',
      price: 0,
      currency: 'USD',
      stock: 0,
      imageUrl: '',
      keywords: [],
      category: DEFAULT_CATEGORY,
      commercialRole: 'auto',
      metadata: {},
    }
  })

  const keywords = watch('keywords')
  const imageUrl = watch('imageUrl')
  const currentPrice = watch('price')
  const currentCurrency = watch('currency')

const loadProducts = useCallback(async () => {
    try {
      setLoading(true)
      const [productsData, categoriesData] = await Promise.all([
        getProducts(),
        categoriesApi.getAll()
      ])
      setProducts(productsData)
      setCategories(categoriesData)
    } catch (error) {
      console.error('Error loading products:', error)
      toast.error('No se pudieron cargar los productos.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadProducts()
  }, [loadProducts])

  // Poll import status
  useEffect(() => {
    if (!importJobId) return

    const interval = setInterval(async () => {
      try {
        const status = await getImportStatus(importJobId)

        if (status.state === 'completed') {
          clearInterval(interval)
          setIsImporting(false)
          setImportJobId(null)
          setImportProgress(null)

          if (status.result) {
            toast.success(
              `Importación completada: ${status.result.insertedCount} nuevos, ${status.result.updatedCount} actualizados. Los roles comerciales se enriquecerán automáticamente con IA en segundo plano.`,
              { duration: 6000 }
            )
          }
          loadProducts()
        } else if (status.state === 'failed') {
          clearInterval(interval)
          setIsImporting(false)
          setImportJobId(null)
          setImportProgress(null)
          toast.error('Error en la importación')
        } else {
          setImportProgress(`Procesando... (${status.state})`)
        }
      } catch (error) {
        console.error('Error checking import status:', error)
      }
    }, 2000)

    return () => clearInterval(interval)
  }, [importJobId, loadProducts])

  const openModal = async (product?: Product) => {
    setSelectedComplementProductId('')
    if (product) {
      setEditingProduct(product)
      const rawRole = (product.commercialRole || '').trim().toLowerCase()
      const normalizedRole =
        rawRole === 'core' || rawRole === 'base' || rawRole === 'principal'
          ? 'main'
          : product.commercialRole || ''

      reset({
        sku: product.sku,
        name: product.name,
        description: product.description || '',
        price: product.priceCents / 100, // Convertir centavos a precio real
        currency: product.currency,
        stock: product.stock,
        imageUrl: product.imageUrl || '',
        keywords: product.keywords ? product.keywords.split(',').map(k => k.trim()) : [],
        category: product.category || DEFAULT_CATEGORY,
        commercialRole: normalizedRole,
        metadata: product.metadata || {},
      })
      setIsModalOpen(true)
      try {
        setLoadingComplements(true)
        const relations = await listProductRelations(product.id)
        setComplements(relations)
      } catch (err) {
        console.error('Error loading product complements:', err)
        setComplements([])
      } finally {
        setLoadingComplements(false)
      }
    } else {
      setEditingProduct(null)
      setComplements([])
      reset({
        sku: '',
        name: '',
        description: '',
        price: 0,
        currency: 'USD',
        stock: (profile.slug === 'salud' || profile.slug === 'inmobiliaria') ? 1 : 0,
        imageUrl: '',
        keywords: [],
        category: DEFAULT_CATEGORY,
        commercialRole: 'auto',
        metadata: {},
      })
      setIsModalOpen(true)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingProduct(null)
    setComplements([])
    setSelectedComplementProductId('')
    reset()
  }

  const handleSuggestRole = async () => {
    const productName = watch('name')?.trim()
    if (!productName) {
      toast.error('Ingresa al menos el nombre del producto para sugerir el rol')
      return
    }
    try {
      setIsSuggestingRole(true)
      const categoryName = watch('category')
      const description = watch('description')
      const price = watch('price')
      const priceCents = price ? Math.round(price * 100) : undefined

      const result = await suggestProductCommercialRole({
        name: productName,
        description: description || undefined,
        categoryName: categoryName && categoryName !== DEFAULT_CATEGORY ? categoryName : undefined,
        priceCents,
      })

      if (result && result.role) {
        setValue('commercialRole', result.role)
        const roleLabel = getRoleLabel(result.role, profile.slug) || result.role
        toast.success(`Sugerido por IA: ${roleLabel}`, {
          duration: 4000,
        })
      }
    } catch (err: any) {
      console.error('Error suggesting commercial role:', err)
      toast.error('No se pudo obtener la sugerencia de IA en este momento.')
    } finally {
      setIsSuggestingRole(false)
    }
  }

  const handleAddComplement = async () => {
    if (!editingProduct || !selectedComplementProductId) return
    try {
      setAddingComplement(true)
      const newRelation = await upsertProductRelation({
        sourceProductId: editingProduct.id,
        targetProductId: selectedComplementProductId,
        relationType: 'complement',
        priority: 10,
        isActive: true,
      })
      const targetProd = products.find((p) => p.id === selectedComplementProductId)
      const relationWithTarget: ProductCatalogRelation = {
        ...newRelation,
        targetProduct: targetProd ? { id: targetProd.id, name: targetProd.name, sku: targetProd.sku } : null,
      }
      setComplements((prev) => [
        ...prev.filter((r) => r.targetProductId !== selectedComplementProductId),
        relationWithTarget,
      ])
      setSelectedComplementProductId('')
      toast.success('Acompañante agregado exitosamente')
    } catch (err) {
      console.error('Error adding complement:', err)
      toast.error('No se pudo agregar el acompañante')
    } finally {
      setAddingComplement(false)
    }
  }

  const handleRemoveComplement = async (relationId: string) => {
    try {
      await deleteProductRelation(relationId)
      setComplements((prev) => prev.filter((r) => r.id !== relationId))
      toast.success('Acompañante removido')
    } catch (err) {
      console.error('Error removing complement:', err)
      toast.error('No se pudo remover el acompañante')
    }
  }

  const onSubmit = async (data: ProductFormValues) => {
    try {
      const isServiceOrAppointment = profile.slug === 'salud' || profile.slug === 'inmobiliaria'

      let rawRole = (data.commercialRole || '').trim().toLowerCase()
      let resolvedRole: string | null = null
      if (rawRole === 'core' || rawRole === 'base' || rawRole === 'principal') {
        resolvedRole = 'main'
      } else if (rawRole && rawRole !== 'auto') {
        resolvedRole = rawRole
      }

      const productData: ProductCreateInput = {
        sku: data.sku.trim(),
        name: data.name.trim(),
        description: data.description?.trim() || undefined,
        priceCents: Math.round(data.price * 100), // Convertir precio real a centavos
        currency: data.currency,
        stock: isServiceOrAppointment ? (data.stock || 1) : data.stock,
        imageUrl: data.imageUrl?.trim() || undefined,
        keywords: keywords.join(','),
        category: data.category || DEFAULT_CATEGORY,
        commercialRole: resolvedRole,
        metadata: data.metadata && Object.keys(data.metadata).length > 0 ? data.metadata : undefined,
      }

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData)
        toast.success('Producto actualizado exitosamente')
      } else {
        await createProduct(productData)
        toast.success('Producto creado exitosamente')
      }
      closeModal()
      loadProducts()
    } catch (error: any) {
      console.error('Error saving product:', error)
      const { message, fieldErrors } = extractApiErrorMessage(error, 'Error al guardar el producto')

      // Mapear errores específicos a los campos del formulario para resaltarlos en rojo
      Object.entries(fieldErrors).forEach(([field, msg]) => {
        setError(field as any, { type: 'server', message: msg })
      })

      toast.error(message, { duration: 6000 })
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este producto?')) return

    try {
      await deleteProduct(id)
      toast.success('Producto eliminado')
      loadProducts()
    } catch (error: any) {
      console.error('Error deleting product:', error)
      toast.error('Error al eliminar el producto')
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      await downloadProductTemplate()
      toast.success('Plantilla descargada')
    } catch (error) {
      console.error('Error downloading template:', error)
      toast.error('Error al descargar la plantilla')
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ]
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Por favor selecciona un archivo Excel (.xlsx o .xls)')
      return
    }

    try {
      setIsImporting(true)
      setImportProgress('Subiendo archivo...')

      const result = await importProductsExcel(file)
      setImportJobId(result.jobId)
      setImportProgress(`Importando ${result.totalRows || 0} productos...`)

      toast.success('Archivo subido, procesando importación...')
    } catch (error: any) {
      console.error('Error importing products:', error)
      toast.error(error?.response?.data?.message || 'Error al importar productos')
      setIsImporting(false)
      setImportProgress(null)
    }

    // Reset file input
    e.target.value = ''
  }

  // Obtener nombres de todas las categorías (incluyendo subcategorías)
  const getAllCategoryNames = (cats: Category[]): string[] => {
    let names: string[] = []
    cats.forEach(c => {
      names.push(c.name)
      if (c.children && c.children.length > 0) {
        names = [...names, ...getAllCategoryNames(c.children)]
      }
    })
    return names
  }

  const allCategories = [
    ...getAllCategoryNames(categories),
    ...products.map(p => p.category || DEFAULT_CATEGORY)
  ]
  const uniqueCategories = Array.from(new Set(allCategories)).sort()

  // Filtrar productos para la vista de tarjetas (memoizado para evitar re-renderizados innecesarios)
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch =
        !searchQuery ||
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.sku.toLowerCase().includes(searchQuery.toLowerCase())
      const matchesCategory =
        !categoryFilter || (p.category || DEFAULT_CATEGORY) === categoryFilter
      return matchesSearch && matchesCategory
    })
  }, [products, searchQuery, categoryFilter])

  // Sincronizar filas del spreadsheet al cargar o actualizar la lista de productos
  useEffect(() => {
    setSpreadsheetRows(products.map(mapProductToSpreadsheetRow))
  }, [products])

  // Filas modificadas o creadas pendientes de guardado
  const dirtySpreadsheetRows = useMemo(() => {
    return getDirtySpreadsheetRows(spreadsheetRows)
  }, [spreadsheetRows])

  const hasSpreadsheetChanges = dirtySpreadsheetRows.length > 0

  // Filas visibles en la vista spreadsheet según filtros de búsqueda y categoría
  const filteredSpreadsheetRows = useMemo(() => {
    return filterSpreadsheetRows(
      spreadsheetRows,
      searchQuery,
      categoryFilter,
      DEFAULT_CATEGORY
    )
  }, [spreadsheetRows, searchQuery, categoryFilter])

  // Manejar cambio inmutable en cualquier celda del spreadsheet
  const handleSpreadsheetRowFieldChange = (
    clientId: string,
    field: keyof SpreadsheetProductRow,
    value: any
  ) => {
    setSpreadsheetRows((prev) =>
      updateSpreadsheetRowField(prev, clientId, field, value)
    )
  }

  // Agregar nueva fila al inicio del spreadsheet
  const handleAddSpreadsheetRow = () => {
    const defaultStock =
      profile.slug === 'salud' || profile.slug === 'inmobiliaria' ? 1 : 0
    const newRow = createEmptySpreadsheetRow(
      categoryFilter || DEFAULT_CATEGORY,
      defaultStock
    )
    setSpreadsheetRows((prev) => [newRow, ...prev])
  }

  // Eliminar o descartar fila del spreadsheet
  const handleDeleteSpreadsheetRow = async (row: SpreadsheetProductRow) => {
    if (row.id) {
      if (confirm(`¿Eliminar permanentemente "${row.name || row.sku}"?`)) {
        await handleDelete(row.id)
      }
    } else {
      setSpreadsheetRows((prev) => removeSpreadsheetRow(prev, row.clientId))
      toast.success('Fila descartada')
    }
  }

  // Descartar cambios sin guardar
  const discardSpreadsheetChanges = () => {
    if (confirm('¿Estás seguro de descartar todos los cambios no guardados?')) {
      setSpreadsheetRows(products.map(mapProductToSpreadsheetRow))
      toast.success('Cambios descartados')
    }
  }

  // Guardar cambios del spreadsheet de forma optimizada (solo filas modificadas)
  const saveSpreadsheetChanges = async () => {
    if (dirtySpreadsheetRows.length === 0) return
    setSavingSpreadsheet(true)
    let successCount = 0
    let errorCount = 0
    const errorDetails: string[] = []

    try {
      for (const row of dirtySpreadsheetRows) {
        const validation = validateSpreadsheetRow(row)
        if (!validation.isValid || !validation.productData) {
          errorCount++
          errorDetails.push(validation.error || 'Datos incompletos')
          continue
        }

        try {
          if (row.id && products.some((p) => p.id === row.id)) {
            // Actualizar producto existente
            await updateProduct(row.id, validation.productData)
          } else {
            // Crear nuevo producto
            await createProduct(validation.productData)
          }
          successCount++
        } catch (err: any) {
          console.error('Error saving spreadsheet product:', err)
          errorCount++
          const { message } = extractApiErrorMessage(err, 'Error al guardar')
          errorDetails.push(`"${row.name}": ${message}`)
        }
      }

      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? '1 producto guardado exitosamente'
            : `${successCount} productos guardados exitosamente`
        )
        await loadProducts()
      }

      if (errorCount > 0) {
        toast.error(
          `Hubo ${errorCount} error(es) al guardar:\n${errorDetails.slice(0, 3).join('\n')}`,
          { duration: 5000 }
        )
      }
    } catch (error) {
      console.error('Error saving spreadsheet changes:', error)
      toast.error('Error al guardar los cambios del catálogo')
    } finally {
      setSavingSpreadsheet(false)
    }
  }

  const tenantName = tenant?.name || 'Tu tienda'
  const publicCatalogUrl = tenantSlug ? getPublicCatalogUrl(tenantSlug) : ''
  const activePromotionsQuery = useQuery({
    queryKey: ['products-active-promotions'],
    queryFn: () => promotionsApi.getPromotions({ state: 'current', limit: 100 }),
    staleTime: 30_000,
  })
  const promotionByProductId = useMemo(() => {
    const entries = (activePromotionsQuery.data?.promotions ?? [])
      .filter((promotion) => promotion.productId && promotion.active && promotion.lifecycleStatus === 'current')
      .map((promotion) => [promotion.productId as string, promotion] as const)
    return new Map(entries)
  }, [activePromotionsQuery.data?.promotions])

  const openPublicCatalog = () => {
    if (!publicCatalogUrl) return
    window.open(publicCatalogUrl, '_blank', 'noopener,noreferrer')
  }

  const copyPublicCatalogLink = async () => {
    if (!publicCatalogUrl) return
    await navigator.clipboard.writeText(publicCatalogUrl)
    toast.success('Enlace copiado')
  }

  const openPublicProduct = (product: Product) => {
    if (!tenantSlug || !product.slug) return
    window.open(getPublicCatalogUrl(tenantSlug, product.slug), '_blank', 'noopener,noreferrer')
  }

  const copyProductLink = async (product: Product) => {
    if (!tenantSlug || !product.slug) return
    await navigator.clipboard.writeText(getPublicCatalogUrl(tenantSlug, product.slug))
    toast.success('Enlace del producto copiado')
  }

  const shareProductByWhatsApp = (product: Product) => {
    if (!tenantSlug || !product.slug) return
    const url = getPublicCatalogUrl(tenantSlug, product.slug)
    const message = buildCatalogShareMessage({
      tenantName,
      productName: product.name,
      priceLabel: formatMoney(product.priceCents, product.currency),
      url,
    })
    window.open(buildWhatsAppShareLink(message), '_blank', 'noopener,noreferrer')
  }


  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-[#075E54]" />
        <span className="ml-3 text-gray-600">Cargando productos...</span>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div data-tour="products-main" className="sticky top-0 z-30 bg-white border-b border-[#075E54]/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-[#075E54]" />
          <div>
            <h1 className="text-lg font-bold leading-none text-gray-900">IridiaChat</h1>
            <p className="text-xs text-slate-500">Panel de {profile.terminology.itemsPluralName}</p>
          </div>
        </div>
        <div data-tour="products-actions" className="flex items-center gap-1">
          <button
            onClick={openPublicCatalog}
            disabled={!publicCatalogUrl}
            className="inline-flex items-center gap-2 rounded-full border border-[#075E54]/15 px-3 py-2 text-xs font-semibold text-[#075E54] hover:bg-[#075E54]/5 disabled:cursor-not-allowed disabled:opacity-50"
            title="Abrir catálogo público"
          >
            <ExternalLink className="w-4 h-4" />
            {profile.terminology.catalogName}
          </button>
          <button
            onClick={copyPublicCatalogLink}
            disabled={!publicCatalogUrl}
            className="inline-flex items-center gap-2 rounded-full border border-[#075E54]/15 px-3 py-2 text-xs font-semibold text-[#075E54] hover:bg-[#075E54]/5 disabled:cursor-not-allowed disabled:opacity-50"
            title="Copiar enlace del catálogo"
          >
            <Copy className="w-4 h-4" />
            Copiar
          </button>
          <button
            onClick={handleDownloadTemplate}
            className="p-2 rounded-full hover:bg-[#075E54]/10 transition-colors"
            title="Descargar plantilla"
          >
            <Download className="w-5 h-5 text-slate-600" />
          </button>
          <button
            onClick={handleImportClick}
            disabled={isImporting}
            className="p-2 rounded-full hover:bg-[#075E54]/10 transition-colors disabled:opacity-50"
            title="Importar Excel"
          >
            {isImporting
              ? <Loader2 className="w-5 h-5 animate-spin text-[#075E54]" aria-label={importProgress || 'Importando...'} />
              : <Upload className="w-5 h-5 text-slate-600" />
            }
          </button>
          <button onClick={loadProducts} className="p-2 rounded-full hover:bg-[#075E54]/10 transition-colors" title="Actualizar">
            <RefreshCw className="w-5 h-5 text-slate-600" />
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* Navigation Tabs + Search + Category Chips */}
      <div className="px-4 pt-4 space-y-3">
        <CatalogNavTabs className="!mb-3" />
        <div data-tour="products-search" className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#075f55]/60" />
          <input
            type="text"
            placeholder="Buscar por nombre, SKU o categoría..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-10 py-3 rounded-xl border-none bg-[#075f55]/5 text-sm outline-none focus:ring-2 focus:ring-[#075f55]/20 placeholder:text-[#075f55]/40 transition-all"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-[#075f55]/10 transition-colors"
              title="Limpiar búsqueda"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setCategoryFilter('')}
            className={`whitespace-nowrap h-9 px-4 rounded-full text-sm font-medium transition-colors flex-shrink-0 flex items-center gap-1.5 ${!categoryFilter ? 'bg-[#075f55] text-white' : 'bg-[#075f55]/5 text-[#075f55] border border-[#075f55]/10 hover:bg-[#075f55]/10'}`}
          >
            <span>Todos</span>
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${!categoryFilter ? 'bg-white/20 text-white' : 'bg-[#075f55]/10 text-[#075f55]'}`}
            >
              {products.length}
            </span>
          </button>
          {uniqueCategories.map((cat) => {
            const count = products.filter(
              (p) => (p.category || DEFAULT_CATEGORY) === cat
            ).length
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter((prev) => (prev === cat ? '' : cat))}
                className={`whitespace-nowrap h-9 px-4 rounded-full text-sm font-medium transition-colors flex-shrink-0 flex items-center gap-1.5 ${categoryFilter === cat ? 'bg-[#075f55] text-white' : 'bg-[#075f55]/5 text-[#075f55] border border-[#075f55]/10 hover:bg-[#075f55]/10'}`}
              >
                <span>{cat}</span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${categoryFilter === cat ? 'bg-white/20 text-white' : 'bg-[#075f55]/10 text-[#075f55]'}`}
                >
                  {count}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {/* View Toggle Pill */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex p-1 bg-slate-200/50 rounded-xl">
          <button
            onClick={() => setViewMode('cards')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-all",
              viewMode === 'cards'
                ? "bg-white shadow-sm font-semibold text-[#075E54]"
                : "font-medium text-slate-500"
            )}
          >
            <LayoutGrid className="w-[18px] h-[18px]" />
            Tarjetas
          </button>
          <button
            onClick={() => setViewMode('spreadsheet')}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm transition-all",
              viewMode === 'spreadsheet'
                ? "bg-white shadow-sm font-semibold text-[#075E54]"
                : "font-medium text-slate-500"
            )}
          >
            <Table2 className="w-[18px] h-[18px]" />
            Spreadsheet
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pb-32">
        {products.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#075E54]/10 shadow-sm p-8 text-center mt-2" data-testid="empty-catalog-state">
            <div className="w-16 h-16 bg-[#075E54]/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-[#075E54]/30" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1" data-testid="empty-catalog-title">
              {profile.terminology.emptyCatalogTitle}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto" data-testid="empty-catalog-desc">
              {profile.terminology.emptyCatalogDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => openModal()} className="bg-[#075E54] hover:bg-[#064E46] text-white">
                <Plus className="w-4 h-4 mr-2" />
                {profile.terminology.createItemCta}
              </Button>
              <Button variant="outline" onClick={handleDownloadTemplate} className="border-[#075E54]/30 text-[#075E54]">
                <Download className="w-4 h-4 mr-2" />
                Descargar Plantilla
              </Button>
            </div>
          </div>
        ) : viewMode === 'spreadsheet' ? (
          <div className="bg-white rounded-xl border border-[#075E54]/10 shadow-sm overflow-hidden mt-2">
            {/* Barra de cambios pendientes (sticky bajo el header) */}
            {hasSpreadsheetChanges && (
              <div className="sticky top-[57px] z-20 bg-amber-50 px-4 py-2.5 flex items-center justify-between border-b border-amber-200/80 shadow-xs">
                <div className="flex items-center gap-2 text-amber-800">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse" />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    {dirtySpreadsheetRows.length}{' '}
                    {dirtySpreadsheetRows.length === 1
                      ? 'cambio pendiente'
                      : 'cambios pendientes'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={discardSpreadsheetChanges}
                    disabled={savingSpreadsheet}
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition disabled:opacity-50"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Descartar
                  </button>
                  <button
                    onClick={saveSpreadsheetChanges}
                    disabled={savingSpreadsheet}
                    className="inline-flex items-center gap-1.5 bg-[#075E54] hover:bg-[#064E46] text-white px-3.5 py-1 rounded-lg text-xs font-bold shadow-sm disabled:opacity-60 transition"
                  >
                    {savingSpreadsheet ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        Guardar cambios
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Barra superior de conteo y botón de agregar fila rápida */}
            <div className="px-4 py-2.5 border-b bg-slate-50/80 flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
                <span>
                  Mostrando{' '}
                  <strong className="text-slate-800">
                    {filteredSpreadsheetRows.length}
                  </strong>{' '}
                  de {spreadsheetRows.length}{' '}
                  {profile.terminology.itemsPluralName.toLowerCase()}
                </span>
                {categoryFilter && (
                  <span className="bg-[#075E54]/10 text-[#075E54] px-2 py-0.5 rounded-full font-semibold">
                    {categoryFilter}
                  </span>
                )}
              </div>
              <button
                onClick={handleAddSpreadsheetRow}
                className="inline-flex items-center gap-1.5 text-[#075E54] hover:text-[#064E46] text-xs font-bold px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/60 transition"
              >
                <PlusCircle className="w-4 h-4 text-[#075E54]" />
                Agregar fila rápida
              </button>
            </div>

            <div data-tour="products-table" className="overflow-x-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-100/80 text-slate-600 font-semibold border-b border-slate-200 text-xs">
                  <tr>
                    <th className="px-3 py-3 w-8 text-center">#</th>
                    <th className="px-3 py-3 w-12 text-center">Foto</th>
                    <th className="px-4 py-3 min-w-[200px]">Nombre *</th>
                    <th className="px-4 py-3 min-w-[140px]">SKU *</th>
                    <th className="px-4 py-3 min-w-[120px]">Precio</th>
                    <th className="px-4 py-3 min-w-[110px]">
                      {profile.terminology.availabilityLabel}
                    </th>
                    <th className="px-4 py-3 min-w-[160px]">Categoría</th>
                    <th className="px-4 py-3 min-w-[180px]">
                      Rol Comercial (Bot IA)
                    </th>
                    <th className="px-3 py-3 w-20 text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {filteredSpreadsheetRows.map((row, index) => {
                    const originalProduct = row.id
                      ? products.find((p) => p.id === row.id)
                      : null
                    const isRowDirty = Boolean(row.isDirty)
                    const isRowNew = Boolean(row.isNew)

                    return (
                      <tr
                        key={row.clientId}
                        className={cn(
                          'transition-colors',
                          isRowNew
                            ? 'bg-emerald-50/50 hover:bg-emerald-50/80'
                            : isRowDirty
                            ? 'bg-amber-50/40 hover:bg-amber-50/70'
                            : 'bg-white hover:bg-slate-50'
                        )}
                      >
                        {/* Estado / Índice */}
                        <td className="px-3 py-2 text-center text-slate-400">
                          {isRowNew ? (
                            <span
                              className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800"
                              title="Nuevo producto"
                            >
                              +
                            </span>
                          ) : isRowDirty ? (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-amber-500"
                              title="Modificado sin guardar"
                            />
                          ) : (
                            <span className="text-[11px] font-mono">
                              {index + 1}
                            </span>
                          )}
                        </td>

                        {/* Miniatura de imagen */}
                        <td className="px-3 py-2 text-center">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center mx-auto">
                            {row.imageUrl ? (
                              <img
                                src={resolveImageUrl(row.imageUrl)}
                                alt={row.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Package className="w-4 h-4 text-slate-300" />
                            )}
                          </div>
                        </td>

                        {/* Nombre */}
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={row.name}
                            onChange={(e) =>
                              handleSpreadsheetRowFieldChange(
                                row.clientId,
                                'name',
                                e.target.value
                              )
                            }
                            className="w-full bg-white/70 hover:bg-white focus:bg-white px-2.5 py-1.5 rounded-lg border border-transparent hover:border-slate-200 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 text-slate-800 font-semibold outline-none transition"
                            placeholder="Nombre del producto..."
                          />
                        </td>

                        {/* SKU */}
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={row.sku}
                            onChange={(e) =>
                              handleSpreadsheetRowFieldChange(
                                row.clientId,
                                'sku',
                                e.target.value.toUpperCase()
                              )
                            }
                            className="w-full bg-white/70 hover:bg-white focus:bg-white px-2.5 py-1.5 rounded-lg border border-transparent hover:border-slate-200 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 font-mono text-xs uppercase text-slate-700 outline-none transition"
                            placeholder={isRowNew ? 'Auto (SKU...)' : 'SKU...'}
                          />
                        </td>

                        {/* Precio */}
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1 bg-white/70 hover:bg-white focus-within:bg-white px-2 py-1.5 rounded-lg border border-transparent hover:border-slate-200 focus-within:border-[#075E54] focus-within:ring-2 focus-within:ring-[#075E54]/20 transition">
                            <span className="text-slate-400 font-semibold">
                              {row.currency === 'EUR' ? '€' : '$'}
                            </span>
                            <input
                              type="number"
                              value={row.price}
                              onChange={(e) =>
                                handleSpreadsheetRowFieldChange(
                                  row.clientId,
                                  'price',
                                  e.target.value
                                )
                              }
                              className="w-20 bg-transparent border-none p-0 text-slate-800 font-bold outline-none"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </td>

                        {/* Stock / Disponibilidad */}
                        <td className="px-4 py-2">
                          <input
                            type="number"
                            value={row.stock}
                            onChange={(e) =>
                              handleSpreadsheetRowFieldChange(
                                row.clientId,
                                'stock',
                                e.target.value
                              )
                            }
                            className={cn(
                              'w-20 px-2.5 py-1.5 rounded-lg bg-white/70 hover:bg-white focus:bg-white border border-transparent hover:border-slate-200 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 outline-none font-semibold transition',
                              Number(row.stock) === 0
                                ? 'text-red-600 font-bold'
                                : 'text-slate-800'
                            )}
                            min="0"
                          />
                        </td>

                        {/* Categoría */}
                        <td className="px-4 py-2">
                          <select
                            value={row.category || DEFAULT_CATEGORY}
                            onChange={(e) =>
                              handleSpreadsheetRowFieldChange(
                                row.clientId,
                                'category',
                                e.target.value
                              )
                            }
                            className="w-full bg-white/70 hover:bg-white focus:bg-white px-2.5 py-1.5 rounded-lg border border-transparent hover:border-slate-200 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 text-[#075E54] font-bold text-xs uppercase outline-none transition"
                          >
                            <option value={DEFAULT_CATEGORY}>
                              {DEFAULT_CATEGORY}
                            </option>
                            {uniqueCategories
                              .filter((c) => c !== DEFAULT_CATEGORY)
                              .map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                          </select>
                        </td>

                        {/* Rol Comercial Adaptado a Vertical */}
                        <td className="px-4 py-2">
                          <select
                            value={row.commercialRole || ''}
                            onChange={(e) =>
                              handleSpreadsheetRowFieldChange(
                                row.clientId,
                                'commercialRole',
                                e.target.value
                              )
                            }
                            className="w-full bg-white/70 hover:bg-white focus:bg-white px-2 py-1.5 rounded-lg border border-transparent hover:border-slate-200 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20 text-slate-700 text-xs outline-none transition"
                          >
                            <option value="">(Sin rol comercial)</option>
                            {commercialRoleConfig.options
                              .filter((opt) => opt.value !== '')
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.icon} {opt.label}
                                </option>
                              ))}
                          </select>
                        </td>

                        {/* Acciones */}
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {originalProduct && (
                              <button
                                type="button"
                                onClick={() => openModal(originalProduct)}
                                className="p-1.5 text-slate-400 hover:text-[#075E54] hover:bg-emerald-50 rounded-lg transition"
                                title="Editar detalles completos (imágenes, combos, triggers)"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteSpreadsheetRow(row)}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title={
                                row.id ? 'Eliminar producto' : 'Descartar fila'
                              }
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}

                  {filteredSpreadsheetRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-12 text-center text-slate-400 text-sm"
                      >
                        {searchQuery || categoryFilter ? (
                          <div className="space-y-2">
                            <p className="font-medium text-slate-600">
                              No se encontraron{' '}
                              {profile.terminology.itemsPluralName.toLowerCase()}{' '}
                              con los filtros actuales.
                            </p>
                            <button
                              onClick={() => {
                                setSearchQuery('')
                                setCategoryFilter('')
                              }}
                              className="text-xs font-semibold text-[#075E54] hover:underline"
                            >
                              Limpiar filtros
                            </button>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-slate-500">
                              No hay{' '}
                              {profile.terminology.itemsPluralName.toLowerCase()}{' '}
                              registrados.
                            </p>
                            <button
                              onClick={handleAddSpreadsheetRow}
                              className="mt-2 text-xs font-bold text-[#075E54] hover:underline"
                            >
                              + Agregar primera fila
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-3 bg-slate-50/60 text-center border-t border-slate-100 flex items-center justify-center gap-2">
              <button
                onClick={handleAddSpreadsheetRow}
                className="text-[#075E54] hover:text-[#064E46] text-xs font-bold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-emerald-50 transition"
              >
                <Plus className="w-4 h-4" /> Agregar otra fila de{' '}
                {profile.terminology.itemSingularName.toLowerCase()}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mt-2">
          {filteredProducts.map((product) => (
            (() => {
              const promotionBadge = getProductPromotionBadge(promotionByProductId.get(product.id) ?? null)

              return (
              <div
                key={product.id}
                className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden"
              >
                <div className="flex p-3 gap-3">
                  <div className="w-20 h-20 bg-[#075E54]/5 rounded-lg overflow-hidden flex-shrink-0">
                    {product.imageUrl ? (
                      <img src={resolveImageUrl(product.imageUrl)} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-[#075E54]/20" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-[#075E54]/70">
                          {product.category || DEFAULT_CATEGORY}
                        </span>
                        {product.commercialRole && (
                          <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200/60 rounded px-1.5 py-0.5 font-medium">
                            {getRoleLabel(product.commercialRole, profile.slug)}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded flex-shrink-0">
                        {product.sku}
                      </span>
                    </div>
                    {promotionBadge ? (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-semibold text-amber-700">
                        <Tag className="h-3 w-3" />
                        {promotionBadge.label}
                        <span className="rounded-full bg-white px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700">
                          {promotionBadge.detail}
                        </span>
                      </div>
                    ) : null}
                    <h3 className="font-bold text-slate-800 truncate mt-0.5">{product.name}</h3>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-lg font-bold text-slate-900">
                        {product.currency} ${(product.priceCents / 100).toLocaleString()}
                      </span>
                      <VerticalAvailabilityBadge stock={product.stock} vertical={profile.slug} />
                    </div>
                    <VerticalProductBadge metadata={product.metadata} vertical={profile.slug} />
                  </div>
                </div>
              <div className="grid grid-cols-2 border-t border-slate-50 sm:grid-cols-3 lg:grid-cols-6">
                <button
                  onClick={() => openPublicProduct(product)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <ExternalLink className="h-4 w-4" /> Público
                </button>
                <button
                  onClick={() => shareProductByWhatsApp(product)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <Share2 className="h-4 w-4" /> WhatsApp
                </button>
                <button
                  onClick={() => copyProductLink(product)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <Copy className="h-4 w-4" /> Copiar
                </button>
                <button
                  onClick={() => navigate(`/app/orders/new?productId=${product.id}`)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <ShoppingCart className="h-4 w-4" /> Orden
                </button>
                <Link
                  to={buildPromotionCreateUrl(product.id)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <Tag className="h-4 w-4" /> Promocionar
                </Link>
                <button
                  onClick={() => openModal(product)}
                  className="flex items-center justify-center gap-1 py-2 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-50"
                >
                  <Edit2 className="h-4 w-4" /> Editar
                </button>
                </div>
              </div>
              )
            })()
          ))}
        </div>
        )}
      </div>

      {/* FAB - Mobile Only */}
      <button
        onClick={() => openModal()}
        className="sm:hidden fixed bottom-20 right-4 bg-[#075E54] text-white flex items-center gap-2 px-5 py-3.5 rounded-full shadow-xl z-40 active:scale-95 transition-transform font-bold text-sm"
      >
        <Plus className="w-4 h-4" />
        <span>{profile.terminology.createItemCta}</span>
      </button>

      {/* Modal de creación/edición */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingProduct ? `Editar ${profile.terminology.itemSingularName}: ${editingProduct.name}` : `Crear ${profile.terminology.itemSingularName}`}
        size="xl"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Columna Izquierda: Información Básica y Comercial */}
            <div className="space-y-4">
              <div className="pb-2 border-b border-slate-100 flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Información Comercial
                </h4>
                {editingProduct && (
                  <span className="text-[11px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">
                    SKU: {editingProduct.sku}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Nombre del producto *
                </label>
                <Input
                  {...register('name')}
                  placeholder="Ej: Hamburguesa Doble Queso, Remera Oversize"
                  className="rounded-xl border-slate-200 text-sm py-2.5 focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
                />
                {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name.message}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    SKU / Código *
                  </label>
                  <Input
                    {...register('sku')}
                    placeholder="PROD-001"
                    className="rounded-xl border-slate-200 font-mono text-sm uppercase py-2.5"
                  />
                  {errors.sku && <p className="text-xs text-red-600 mt-1">{errors.sku.message}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Categoría
                  </label>
                  <select
                    {...register('category')}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white transition"
                  >
                    <option value="">Sin Categoría</option>
                    {uniqueCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-xs text-red-600 mt-1 font-medium">{errors.category.message}</p>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                    <Bot className="w-3.5 h-3.5 text-amber-500" />
                    Rol Comercial (para el Bot de IA)
                  </label>
                  <button
                    type="button"
                    onClick={handleSuggestRole}
                    disabled={isSuggestingRole}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 px-2 py-0.5 rounded-lg transition disabled:opacity-50"
                    title="Analizar nombre, descripción y categoría para sugerir el rol con IA"
                  >
                    {isSuggestingRole ? (
                      <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />
                    ) : (
                      <Brain className="w-3 h-3 text-emerald-600" />
                    )}
                    <span>{isSuggestingRole ? 'Analizando...' : '✨ Sugerir con IA'}</span>
                  </button>
                </div>
                <select
                  {...register('commercialRole')}
                  className={cn(
                    "w-full rounded-xl border px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 outline-none bg-white transition",
                    errors.commercialRole ? "border-red-500 focus:border-red-500" : "border-slate-200 focus:border-[#075E54]"
                  )}
                >
                  <option value="auto">✨ Auto (Clasificar con IA al guardar)</option>
                  {commercialRoleConfig.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.icon} {opt.label}
                    </option>
                  ))}
                </select>
                {errors.commercialRole && (
                  <p className="text-xs text-red-600 mt-1 font-medium">{errors.commercialRole.message}</p>
                )}
                <p className="text-[11px] text-slate-400 mt-1">
                  {commercialRoleConfig.helperText}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Descripción
                </label>
                <textarea
                  {...register('description')}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none transition bg-white"
                  placeholder="Detalles del producto, ingredientes, especificaciones o garantías..."
                />
                {errors.description && (
                  <p className="text-xs text-red-600 mt-1 font-medium">{errors.description.message}</p>
                )}
              </div>
            </div>

            {/* Columna Derecha: Precios, Inventario, Imagen & Triggers */}
            <div className="space-y-4">
              <div className="pb-2 border-b border-slate-100 flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Precios e Inventario
                </h4>
                {currentPrice > 0 && (
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                    {formatMoney(Math.round((currentPrice || 0) * 100), currentCurrency || 'USD')}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Moneda
                  </label>
                  <select
                    {...register('currency')}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white font-semibold transition"
                  >
                    <option value="USD">USD ($)</option>
                    <option value="EUR">EUR (€)</option>
                    <option value="ARS">ARS ($)</option>
                    <option value="MXN">MXN ($)</option>
                    <option value="COP">COP ($)</option>
                  </select>
                </div>

                <div className={profile.slug === 'salud' || profile.slug === 'inmobiliaria' ? "sm:col-span-2" : "sm:col-span-1"}>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                    Precio *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    {...register('price', { valueAsNumber: true })}
                    placeholder="99.99"
                    className="rounded-xl border-slate-200 text-sm py-2.5 font-bold"
                  />
                  {errors.price && <p className="text-xs text-red-600 mt-1">{errors.price.message}</p>}
                </div>

                {profile.slug !== 'salud' && profile.slug !== 'inmobiliaria' && (
                  <div className="sm:col-span-1">
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                      Stock *
                    </label>
                    <Input
                      type="number"
                      {...register('stock', { valueAsNumber: true })}
                      placeholder="10"
                      className="rounded-xl border-slate-200 text-sm py-2.5 font-semibold"
                    />
                    {errors.stock && <p className="text-xs text-red-600 mt-1">{errors.stock.message}</p>}
                  </div>
                )}
              </div>

              {/* Multimedia & Live Preview */}
              <div className="pt-1">
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Imagen del Producto
                </label>
                <ProductImageUploader
                  value={imageUrl}
                  onChange={(val) => {
                    setValue('imageUrl', val, { shouldDirty: true, shouldValidate: true })
                  }}
                  error={errors.imageUrl?.message}
                />
              </div>

              {/* Keywords / Triggers */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">
                  Palabras Clave / Triggers (para el Bot)
                </label>
                <TagsInput
                  tags={keywords}
                  onChange={(tags) => setValue('keywords', tags)}
                  placeholder="Escribe palabra y presiona Enter..."
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  El asistente detectará estas palabras para cotizar o recomendar este producto en WhatsApp.
                </p>
              </div>
            </div>
          </div>

          {/* Bloque de Atributos Específicos por Vertical */}
          <VerticalProductFields
            slug={profile.slug}
            values={watch('metadata') || {}}
            onChange={(field, val) =>
              setValue('metadata', { ...(watch('metadata') || {}), [field]: val })
            }
          />

          {/* Bloque de Garantía por Producto (Garantía de Tienda, Fabricante, etc.) */}
          <ProductWarrantyFieldGroup
            value={watch('metadata')?.warranty}
            onChange={(warranty) => {
              const currentMeta = watch('metadata') || {}
              setValue('metadata', { ...currentMeta, warranty }, { shouldDirty: true })
            }}
          />

          {/* Bloque Inferior: Acompañantes y Combos sugeridos (Cross-selling amigable) */}
          {editingProduct ? (
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl bg-[#075E54] text-white flex items-center justify-center shadow-xs">
                    <Zap className="w-4 h-4" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900">{commercialRoleConfig.complementsTitle}</h4>
                    <p className="text-xs text-gray-500">
                      {commercialRoleConfig.complementsDescription}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-bold text-[#075E54] bg-emerald-100/80 px-2.5 py-0.5 rounded-full border border-emerald-200">
                  {complements.length} {complements.length === 1 ? 'sugerido' : 'sugeridos'}
                </span>
              </div>

              {/* Lista de acompañantes asignados */}
              {loadingComplements ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#075E54]" />
                  Cargando acompañantes...
                </div>
              ) : complements.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                  {complements.map((rel) => {
                    const complementProd = products.find((p) => p.id === rel.targetProductId) || rel.targetProduct
                    const complementPrice =
                      complementProd && 'priceCents' in complementProd
                        ? formatMoney((complementProd as any).priceCents, (complementProd as any).currency || 'USD')
                        : null
                    return (
                      <div
                        key={rel.id}
                        className="flex items-center justify-between rounded-xl border border-emerald-200/60 bg-white px-3 py-2 text-xs shadow-xs"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                          <span className="font-semibold text-gray-800 truncate">
                            {complementProd?.name || 'Producto relacionado'}
                          </span>
                          {complementPrice && (
                            <span className="text-[11px] text-emerald-600 font-medium">({complementPrice})</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveComplement(rel.id)}
                          className="text-gray-400 hover:text-red-500 transition p-1 rounded-md hover:bg-red-50 flex-shrink-0"
                          title="Quitar sugerencia"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic py-1">
                  Aún no has vinculado acompañantes para este producto. Selecciona uno abajo para que el bot lo ofrezca al cliente.
                </p>
              )}

              {/* Selector para agregar acompañante con un clic */}
              <div className="flex items-center gap-2 pt-1">
                <select
                  value={selectedComplementProductId}
                  onChange={(e) => setSelectedComplementProductId(e.target.value)}
                  className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-[#075E54] focus:border-transparent outline-none"
                >
                  <option value="">Seleccionar producto para sugerir...</option>
                  {products
                    .filter((p) => p.id !== editingProduct.id && !complements.some((c) => c.targetProductId === p.id))
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} ({formatMoney(p.priceCents, p.currency || 'USD')}) {p.commercialRole ? `· ${p.commercialRole}` : ''}
                      </option>
                    ))}
                </select>
                <Button
                  type="button"
                  size="sm"
                  disabled={!selectedComplementProductId || addingComplement}
                  onClick={handleAddComplement}
                  className="bg-[#075E54] hover:bg-[#064E46] text-white text-xs px-3.5 py-2 rounded-xl flex items-center gap-1.5 flex-shrink-0 font-semibold shadow-xs"
                >
                  {addingComplement ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Agregar
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-3.5 flex items-center gap-2.5 text-xs text-slate-500">
              <Zap className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>
                <strong>Sugerencias de combos para el bot:</strong> Podrás asociar acompañantes, bebidas y adicionales sugeridos inmediatamente después de crear este producto.
              </span>
            </div>
          )}

          {/* Footer del Modal */}
          <div className="flex flex-col-reverse sm:flex-row justify-between gap-3 pt-4 border-t border-slate-100">
            {editingProduct ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => { closeModal(); handleDelete(editingProduct.id) }}
                className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl font-medium"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Eliminar Producto
              </Button>
            ) : <div />}
            <div className="flex gap-2.5 sm:ml-auto">
              <Button type="button" variant="outline" onClick={closeModal} className="rounded-xl">
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-[#075E54] hover:bg-[#064E46] text-white font-semibold rounded-xl shadow-sm px-5">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}
